import { useState } from 'react'
import {
  KeyFob,
  Colour,
  NormalizedColours,
  EngineSize,
  EngineSizes,
  FuelType,
  NormalizedFuelTypes,
  TransmissionType,
  NormalizedTransmissionTypes,
  Doors,
  NormalizedDoors,
  DriveTrain,
  NormalizedDriveTrains,
  BodyStyle,
  NormalizedBodyStyles,
  VehicleOptionType,
  VehicleOptionLabels,
  Answer,
  UpholsteryType,
  NormalizedUpholsteryTypes,
} from '../../../api-client/enums'
import Tabs from '../../../components/tabs'
import useVehiclePageData from '../use-vehicle-page-data'
import Features from './features'
import Equipment from './equipment'
import InstalledOptionPackages from './installed-option-packages'

function generateVehicleDetails(vehicleData) {
  var itemsToDisplay = [
    { title: 'VIN', value: vehicleData?.vin },
    { title: 'Mileage', value: `${vehicleData?.mileage.toLocaleString()} mi` },
  ]

  //
  let engineDescription = ''

  if (vehicleData?.engineDisplacement > 0) {
    engineDescription += vehicleData?.engineDisplacement + 'L '
  }

  if (vehicleData?.engineSize != null && vehicleData?.engineSize != EngineSize.None) {
    engineDescription += EngineSizes[vehicleData?.engineSize] + ' Cyl '
  }

  if (vehicleData?.fuelType != null && vehicleData?.fuelType != FuelType.None) {
    engineDescription += NormalizedFuelTypes[vehicleData?.fuelType]
  }

  if (engineDescription && engineDescription != '') {
    itemsToDisplay.push({
      title: 'Engine',
      value: engineDescription,
    })
  }
  //

  //
  let transmissionDescription = ''

  if (vehicleData?.transmissionSpeed > 0) {
    transmissionDescription += vehicleData?.transmissionSpeed + ' Speed '
  }

  if (
    vehicleData?.transmissionType != null &&
    vehicleData?.transmissionType != TransmissionType.None
  ) {
    transmissionDescription += NormalizedTransmissionTypes[vehicleData?.transmissionType]
  }

  if (!!transmissionDescription && transmissionDescription != '') {
    itemsToDisplay.push({
      title: 'Transmission',
      value: transmissionDescription,
    })
  }
  //

  if (vehicleData?.drivetrain != null && vehicleData?.drivetrain != DriveTrain.None) {
    itemsToDisplay.push({
      title: 'Drivetrain',
      value: NormalizedDriveTrains[vehicleData?.drivetrain],
    })
  }

  if (vehicleData?.doors != null && vehicleData?.doors != Doors.None) {
    itemsToDisplay.push({
      title: 'Doors',
      value: NormalizedDoors[vehicleData?.doors],
    })
  }

  if (vehicleData?.exteriorColour != null && vehicleData?.exteriorColour != Colour.None) {
    itemsToDisplay.push({
      title: 'Exterior Color',
      value: NormalizedColours[vehicleData?.exteriorColour],
    })
  }

  if (vehicleData?.interiorColour != null && vehicleData?.interiorColour != Colour.None) {
    itemsToDisplay.push({
      title: 'Interior Color',
      value: NormalizedColours[vehicleData?.interiorColour],
    })
  }

  if (vehicleData?.upholsteryType != null && vehicleData?.upholsteryType != UpholsteryType.None) {
    itemsToDisplay.push({
      title: 'Upholstery Type',
      value: NormalizedUpholsteryTypes[vehicleData?.upholsteryType],
    })
  }

  if (vehicleData?.keyFob != null && vehicleData?.keyFob != KeyFob.None) {
    itemsToDisplay.push({ title: 'Number of Keys', value: vehicleData?.keyFob })
  }

  if (vehicleData?.bodyStyle != null && vehicleData?.bodyStyle != BodyStyle.None) {
    itemsToDisplay.push({
      title: 'Body Style',
      value: NormalizedBodyStyles[vehicleData?.bodyStyle],
    })
  }

  return itemsToDisplay
}

const generateVehicleOptions = (vehicleData) => {
  const options = {
    [VehicleOptionType.Comfort]: {
      label: 'Comfort',
      children: [],
    },
    [VehicleOptionType.Convenience]: {
      label: 'Convenience',
      children: [],
    },
    [VehicleOptionType.Exterior]: {
      label: 'Exterior',
      children: [],
    },
    [VehicleOptionType.MediaCommunication]: {
      label: 'Media & Communication',
      children: [],
    },
    [VehicleOptionType.Power]: {
      label: 'Power',
      children: [],
    },
    [VehicleOptionType.SafetyAndSecurity]: {
      label: 'Safety & Security',
      children: [],
    },
  }

  ;(vehicleData?.vehicleOptions || []).forEach((entry) => {
    if (options[entry.vehicleOptionType] && entry.optionValue === Answer.Yes) {
      options[entry.vehicleOptionType].children.push({
        label: VehicleOptionLabels[entry.optionName],
      })
    }
  })

  return Object.values(options)
}

export default function Details() {
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const { vehicleData } = useVehiclePageData()

  const tabHeadings = ['Features', 'Equipment', 'Installed Packages']

  return (
    <>
      <div className='detailsContainerWrapper flex justifyCenter flexDirectionColumn'>
        <h2 className='detailsHeading'>Vehicle Details</h2>

        <div className='tabsWrapper'>
          <Tabs
            activeIndex={activeTabIndex}
            onChange={(newIndex) => setActiveTabIndex(newIndex)}
            tabs={tabHeadings}
          />
        </div>

        {activeTabIndex === 0 && <Features features={generateVehicleDetails(vehicleData)} />}
        {activeTabIndex === 1 && <Equipment equipment={generateVehicleOptions(vehicleData)} />}
        {activeTabIndex === 2 && (
          <InstalledOptionPackages
            installedOptionPackages={vehicleData?.installedOptionPackages ?? []}
          />
        )}
      </div>

      <style jsx>{`
        .detailsHeading {
          margin: 5.2rem 0 3.4rem;
          font-weight: 700;
          font-size: 3.2rem;
          line-height: 4.3rem;
        }

        .tabsWrapper {
          margin: 0 auto 4.75rem;
          max-width: 90rem;
          width: 100%;
        }

        @media (min-width: 1025px) and (max-width: 1364px) {
          .detailsHeading {
            margin: 3.4rem 0 1.3rem;
            font-size: 2rem;
            line-height: 2.7rem;
          }

          .tabsWrapper {
            margin: 0 0 4.2rem;
          }
        }

        @media (max-width: 1024px) {
          .detailsHeading {
            padding: 4rem 0 2rem;
            width: 100%;
            margin: 0 auto;
            font-size: 2rem;
            line-height: 2.7rem;
          }

          .tabsWrapper {
            margin: 0 0 0.3rem;
          }
        }
      `}</style>
    </>
  )
}
